import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api, draftStream } from '../lib/api';
import { redact } from '../lib/privacy';
import { Ticket } from '../lib/types';
import { WorkspaceTicketState } from './useWorkspaceTicket';

async function copyApprovedReply(reply: string) {
  try {
    await navigator.clipboard.writeText(reply);
    toast.success('Approved and copied. Ready to send from your inbox.');
  } catch {
    toast.warning('Reply approved. Clipboard access is blocked; select and copy the reply manually.');
  }
}

export function useWorkspaceActions(id: string | undefined, state: WorkspaceTicketState, refresh: () => Promise<void>) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef<AbortController | null>(null);
  const mutations = useRef(new Set<AbortController>());
  const dirty = Boolean(state.ticket && state.draft !== state.ticket.draft);

  useEffect(() => {
    setError(''); setBusy(false); setSaving(false);
    const pendingMutations = mutations.current;
    return () => {
      generation.current?.abort();
      pendingMutations.forEach(controller => controller.abort());
      pendingMutations.clear();
    };
  }, [id]);
  useEffect(() => {
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!busy && !dirty) return;
      event.preventDefault(); event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [busy, dirty]);

  const generate = async (regenerate = false) => {
    if (busy) return;
    if (regenerate && !state.ticket) return;
    if (regenerate && dirty && !window.confirm('Replace your unsaved edits with a new draft?')) return;
    const controller = new AbortController();
    generation.current = controller;
    setBusy(true); setStage('Protecting personal details…'); setError('');
    const path = regenerate ? `/tickets/${state.ticket!.id}/regenerate` : '/tickets';
    const body = regenerate ? null : {message: redact(state.message).text, title: redact(state.title).text, channel: state.channel, tone: state.tone};
    try {
      const ticket = await draftStream(path, body, setStage, controller.signal);
      state.load(ticket);
      if (!id) navigate(`/ticket/${ticket.id}`, {replace: true});
      await refresh();
      if (ticket.error) toast.error('Ticket saved. Drafting needs a retry.');
      else toast.success(ticket.needs_human ? 'Ticket triaged. Human review needed.' : 'Your grounded reply is ready.');
    } catch (error: any) {
      if (!controller.signal.aborted) { setError(error.message); toast.error(error.message); }
    } finally { if (!controller.signal.aborted) setBusy(false); }
  };

  const patch = async (changes: Partial<Ticket>) => {
    if (!state.ticket || saving) return;
    const controller = new AbortController();
    mutations.current.add(controller);
    setSaving(true);
    try {
      const ticket = await api<Ticket>(`/tickets/${state.ticket.id}`, 'PATCH', changes, controller.signal);
      if (controller.signal.aborted) return;
      state.setTicket(ticket);
      if ('draft' in changes) state.setDraft(ticket.draft);
      await refresh();
      if (changes.feedback) toast.success('Feedback saved. Thank you!');
      if (changes.status === 'resolved') toast.success('Ticket resolved. Nicely handled.');
      if ('draft' in changes) toast.success('Draft changes saved.');
    } catch (error: any) { if (!controller.signal.aborted) toast.error(error.message); }
    finally { mutations.current.delete(controller); if (!controller.signal.aborted) setSaving(false); }
  };

  const approve = async () => {
    if (!state.ticket || saving) return;
    const controller = new AbortController();
    mutations.current.add(controller);
    setSaving(true);
    try {
      const ticket = await api<Ticket>(`/tickets/${state.ticket.id}`, 'PATCH', {draft: state.draft, status: 'approved'}, controller.signal);
      if (controller.signal.aborted) return;
      state.load(ticket);
      await copyApprovedReply(ticket.draft);
      await refresh();
    } catch (error: any) { if (!controller.signal.aborted) toast.error(error.message); }
    finally { mutations.current.delete(controller); if (!controller.signal.aborted) setSaving(false); }
  };

  const newTicket = () => {
    if (dirty && !window.confirm('Discard your unsaved draft changes?')) return;
    if (!state.ticket && state.message && !window.confirm('Clear your unsaved customer message?')) return;
    setError('');
    if (id) navigate('/');
    else state.reset();
  };
  return {busy, stage, saving, error, setError, generate, patch, approve, newTicket};
}